// Copyright (c) 2023, Comsearch, Inc., All rights reserved

import { MUST_SEARCH_AT_LEAST_ONE_VALUE } from 'errors'
import { valueAsKm } from 'helpers/unitConversion'
import { convertDMStoDD } from 'helpers/coordsConversion'
import { PathStatusIdEnum } from 'config/enums'
import makeSiteSearchFieldsForPathSearch from './makeSiteSearchFieldsForPathSearch'

/**
 * @typedef {Object} SearchObject
 * @property {string} field
 * @property {string | number} value
 * @property {'LIKE' | 'NOT' | 'EQ'} operator
 */

/**
 *
 * @param {SearchObject[]} search
 * @param {string} field
 * @param {string | number} value
 * @param {'EQ' | 'LIKE' | 'NOT'} operator
 */
const addFieldAndValueToSearch = (search, field, value, operator) => {
  if (value) {
    search.push({
      field,
      operator,
      value,
    })
  }
}

const handleAntennaField = (data, query) => {
  if (data.antennaField === 'antennaCode') {
    addFieldAndValueToSearch(query, 'antennaSpecId', data.antennaSpecId, 'EQ')
  } else if (data.antennaField === 'antennaCompanyId') {
    const antennaCompanyId = (
      data.antennaCompanyId
        .map(({ companyId }) => companyId)
        .join(',')
    )
    addFieldAndValueToSearch(query, 'antennaCompanyId', antennaCompanyId, 'EQ')
  } else {
    throw new Error('Error: Antenna Field was expected to be Antenna Manufacturer or Antenna Code but was provided as ' + data.antennaField)
  }
}


const handleRadioCode = (data, query) => {
  if (data.radioFavoriteId !== null) {
    addFieldAndValueToSearch(query, 'radioFavoriteId', data.radioFavoriteId, 'EQ')
  } else if (data.radioId !== null) {
    addFieldAndValueToSearch(query, 'radioId', data.radioId, 'EQ')
  }
}

const handleRadioCompanyId = (data, query) => {
  const radioCompanyId = (
    data.radioCompanyId
      .map(({ companyId }) => companyId)
      .join(',')
  )

  addFieldAndValueToSearch(query, 'radioCompanyId', radioCompanyId, 'LIKE')
}

const handleRadioField = (data, query) => {
  if (data.radioField === 'radioCompanyId') {
    handleRadioCompanyId(data, query)
  } else if (data.radioField === 'radioCode') {
    handleRadioCode(data, query)
  } else {
    throw new Error('Error: Radio Field was expected to be Radio Manufacturer or Radio Code but was provided as ' + data.radioField)
  }
}

const handleGeoSearch = (data, search) => {
  if (data.latitude && data.longitude) {
    const radiusKm = valueAsKm(data.radius, data.radiusUnit)

    // default geo search radius to 0m
    const radiusM = (
      radiusKm === null
        ? 0.1
        : radiusKm * 1000
    )

    search.push({
      field: 'geoSearch',
      latitude: convertDMStoDD(data.latitude),
      longitude: convertDMStoDD(data.longitude),
      operator: 'EQ',
      value: radiusM,
    })
  }
}

// these fields should user LIKE as their operator.
// all others will use EQ
const operatorLIKE = [
  'asr',
  'billcode',
  'callSign',
  'jobCode',
  'pathName',
  'rcnNumber',
  'siteName',
  'ulsFileNumber',
  'areaOfOperation',
]

const operatorEQ = [
  'frequency',
  'polarizationId',
  'protectFlag',
]

const multiSelectValuesToJoin = [
  'bandId',
  'contactId',
  'licenseBasisId',
  'segmentStatusId',
  'stateId',
]

const makePathDbFormQuery = formValues => {
  // This ensures that we're always searching for paths which aren't "In Progress"
  /**
   *
   * @type {SearchObject[]}
   */
  const search  = [{
    field: 'pathStatusId',
    operator: 'NOT',
    value: PathStatusIdEnum.IN_PROGRESS,
  }]

  // track if we add search parameters or not
  const queryInitialSize = search.length

  operatorLIKE.forEach(field => {
    addFieldAndValueToSearch(
      search,
      field,
      formValues[field],
      'LIKE'
    )
  })

  operatorEQ.forEach(field => {
    addFieldAndValueToSearch(
      search,
      field,
      formValues[field],
      'EQ'
    )
  })

  multiSelectValuesToJoin.forEach(field => {
    addFieldAndValueToSearch(
      search,
      field,
      // field name is the same as the property we want to extract from the entity. (eg. band.bandId for input 'bandId')
      formValues[field].map(v => v[field]).join(','),
      'EQ'
    )
  })

  makeSiteSearchFieldsForPathSearch(formValues, search)

  handleRadioField(formValues, search)

  handleAntennaField(formValues, search)

  handleGeoSearch(formValues, search)

  // make sure we're going to search against at least SOMETHING
  if (queryInitialSize === search.length) {
    throw new Error(MUST_SEARCH_AT_LEAST_ONE_VALUE)
  }


  return {
    field: 'search',
    value: [search],
  }
}

export default makePathDbFormQuery
